import { Callout, Collapsible, Skeleton } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking/tracking";
import { isActionAvailable } from "@/utils/available-actions";
import { MessageSquareMore } from "lucide-react";
import { useState } from "react";
import {
  CareplanAvailableAction,
  useCarePlanSummary,
} from "../network/useCarePlanSummary";
import { useFollowUp } from "../network/useFollowUp";
import { AddFollowUp } from "./AddFollowUp";
import { FollowUp } from "./FollowUp";
import { FollowUpOutreachBanner } from "./FollowUpOutreachBanner";

interface FollowUpsProps {
  carePlanId: string;
  childEncounterId: string;
  totalFollowUps: number;
}

export const FollowUps = ({
  carePlanId,
  childEncounterId,
  totalFollowUps,
}: FollowUpsProps) => {
  const [open, setOpen] = useState(false);
  const { data, isFetching, isError } = useFollowUp({
    encounterId: childEncounterId,
    carePlanId,
    enabled: open,
  });

  const { data: careplanSummary } = useCarePlanSummary(childEncounterId);
  const careplanAvailableActions = careplanSummary?.availableActions;

  const canReadFollowUps = isActionAvailable(
    CareplanAvailableAction.READ_FOLLOW_UP,
    careplanAvailableActions,
  );

  if (!canReadFollowUps) {
    return null;
  }

  if (isError) {
    return (
      <Callout
        className="w-full"
        variant="danger"
        title="Something went wrong!"
        description="Failed to load follow-ups."
      />
    );
  }

  return (
    <Collapsible
      title="Follow Up"
      titleIcon={<MessageSquareMore className="w-4 h-4" />}
      headerSize="sm"
      onOpenChange={(open) => {
        trackEvent(
          open ? TRACKERS.EXPAND_FOLLOW_UP : TRACKERS.COLLAPSE_FOLLOW_UP,
        );
        setOpen(open);
      }}
      status={totalFollowUps ? `${totalFollowUps.toString()}` : "None"}
    >
      <div className="flex flex-col">
        <FollowUpOutreachBanner
          carePlanOutreachEnabled={data?.carePlanOutreachEnabled}
        />
        {isFetching ? (
          <div className="grid gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
              <Skeleton className="h-6" key={i} />
            ))}
          </div>
        ) : data?.followupDurationValue ? (
          <FollowUp
            followup={data}
            carePlanId={carePlanId}
            childEncounterId={childEncounterId}
          />
        ) : (
          <AddFollowUp
            carePlanId={carePlanId}
            childEncounterId={childEncounterId}
          />
        )}
      </div>
    </Collapsible>
  );
};
